import React, { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { Layout } from 'app/theme/layout'
import { Helmet } from 'react-helmet-async'
import { useFeatureFlagVariantKey } from 'lib/posthog'
import { format } from 'date-fns'

import { googleMetaTags } from './HomePageSchema'
import LazyImage from 'app/theme/LazyImage'
import EventTypes from '../theme/sections/EventTypes'
import Guarantee from '../theme/sections/Guarantee'
import HowItWorksCustom from '../theme/sections/HowItWorksCustom'
import Cuisines from '../theme/sections/Cuisines'
import FindATruck from '../theme/sections/FindATruck'
import Blog from '../theme/sections/Blog'
import OfficeMap from './homepage/OfficeMap'
import { useAppContext } from 'app/AppContext'
import CityAutoComplete from 'app/theme/forms/CityAutoComplete'
import LeadModal from 'app/leads/LeadModal'
import { useModalState } from 'lib/modal'
import { useSessionRecording } from 'lib/posthog'

import howItWorksLarge from 'assets/img/how-it-works-large.jpg'

import banner from 'assets/img/food-truck-catering.jpg'
import corporateEvent from 'assets/img/large-event-scaled.jpeg'
import campusEvent from 'assets/img/food-truck-campus-events.jpg'
import conferenceCatering from 'assets/img/conference-catering.jpg'

import donutTruck from 'assets/svg/donut-truck.svg'
import forkKnifeMarker from 'assets/svg/map-icon.svg'
import burger from 'assets/img/business-lunch-food-court-hero.jpg'
import guarantee from 'assets/svg/guarantee.svg'
import computer from 'assets/svg/computer.svg'
import partyHat from 'assets/svg/party-hat.svg'
import graduationCap from 'assets/svg/graduation-cap.svg'
import filmCamera from 'assets/svg/film.svg'
import weddingCake from 'assets/svg/wedding-cake.svg'
import balloons from 'assets/svg/balloons.svg'
import mic from 'assets/svg/mic.svg'
import tagIcons from 'app/utils/tagIcons'

import losAngeles from 'assets/img/los-angeles.jpg'
import atlanta from 'assets/img/atlanta.jpg'
import dallas from 'assets/img/dallas.jpg'
import chicago from 'assets/img/chicago.jpg'
import sanJose from 'assets/img/san-jose.jpg'
import washington from 'assets/img/washington-dc.jpg'

import apple from 'assets/img/press/apple.png'
import toyota from 'assets/img/press/toyota.png'
import google from 'assets/img/press/google.png'
import lululemon from 'assets/img/press/lululemon.png'
import disney from 'assets/img/press/disney.png'
import nike from 'assets/img/press/nike.png'

import env from 'env'

const eventIcons = {
  'corporate': computer,
  'private': partyHat,
  'campus': graduationCap,
  'production': filmCamera,
  'wedding': weddingCake,
  'community': balloons,
}

const fetchBlogLinks = () => (
  fetch(`${env.BLOG_URL}/feed/json`).then(res => res.ok ? res.json() : null)
)

const MAX_BLOG_POSTS = 3

export default function HomePage() {
  const [selectedCity, setSelectedCity]  = useState()
  const context = useAppContext()
  const [blogData, setBlogData] = useState(context.blogLinks)
  const { openModal, ...modalState } = useModalState()
  useSessionRecording()

  let maxBlogPostsUsed = MAX_BLOG_POSTS

  if (typeof window === 'undefined' && context.blogLinks === undefined) {
    context.promises = context.promises || []
    context.promises.push(
      fetchBlogLinks().then(data => {
        context.blogLinks = data
      }).catch(() => {
        context.blogLinks = null
      })
    )
  }

  useEffect(() => {
    if (blogData === undefined) {
      fetchBlogLinks().then(data => {
        setBlogData(data)
      }).catch(() => {})
    }
  }, [])

  let blogPosts = blogData?.items?.filter(b => b.tags.includes('Homepage')).sort((a, b) => new Date(b.date_published) - new Date(a.date_published)).slice(0, maxBlogPostsUsed)

  return (
    <Layout>
      <Helmet>
        <title>Roaming Hunger - Food Truck Catering, Promotions and Events</title>
        <meta name="description" content="Roaming Hunger is the easiest way to find and book over 23,000 food trucks for corporate catering, big events and branded promotions." />
        <meta name="og:title" content="Roaming Hunger - Food Truck Catering, Promotions and Events"></meta>
        <script type="application/ld+json">
          {JSON.stringify(googleMetaTags)}
        </script>
      </Helmet>

      <LeadModal
        {...modalState}
      />

      <div className="bg-blue">
        <div className="xl3:max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2">
          <div className="bg-cover bg-center h-64 lg:h-full w-full" style={{backgroundImage: `url(${banner})`}}/>
          <div className="flex flex-col justify-center bg-blue p-8 lg:p-16 max-w-xl">
            <h1 className="text-4xl lg:text-5xl font-platform text-black font-bold leading-tight">
              The Best Food Trucks Make The Best Events
            </h1>
            <p className="text-white text-xl font-light py-4">
              Your <strong className='strong'>peace of mind</strong> is our top priority — we take care of all the tough stuff, 
              so you can enjoy an amazing event.
            </p>
            <div className="mt-4">
              <a
                href="/food-truck-catering/"
                className="btn btn-white text-blue border-0 px-4 py-2 rounded-full inline-block">
                Book Food Truck Catering {`>`}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white py-16">
        <div className="container max-w-5xl">
          <h2 className="text-2xl md:text-4xl font-light md:font-normal text-black font-platform text-left lg:text-center">
            You're in Great Company.
          </h2>
          <p className="text-black text-xl text-left lg:text-center font-light py-4">
            20 million eaters can’t be wrong! Let us help you create an unforgettable food truck experience. 
          </p>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-12">
            <img src={apple} alt="Apple logo" className="m-auto h-12 md:h-14 w-auto"/>
            <img src={nike} alt="Nike logo" className="m-auto h-12 md:h-14 w-auto"/>
            <img src={disney} alt="Disney logo" className="m-auto h-12 md:h-14 w-auto"/>
            <img src={lululemon} alt="Lululemon logo" className="m-auto h-12 md:h-14 w-auto"/>
            <img src={toyota} alt="Toyota logo" className="m-auto h-12 md:h-14 w-auto"/>
            <img src={google} alt="Google logo" className="m-auto h-12 md:h-14 w-auto"/>
          </div>
        </div>
      </div>
      <HowItWorksCustom
        buttonUrl='/food-truck-catering/'
        description={<p className='font-light text-xl'>Welcome to the easiest catering experience...ever. From food trucks and trailers, to pop-ups and carts, Roaming Hunger helps you create amazing events <strong className='font-bold'>anywhere, anytime</strong>.</p>}
        heading='How to Book with Us'
        hiddenLg={false}
        image={{
          url: howItWorksLarge,
          altText: 'People enjoying a food truck meal'
        }}
      />
      <Guarantee
        buttonUrl='/food-truck-catering/'
        items={null}
      />
      <EventTypes openModal={openModal} />
      <Cuisines />
      <FindATruck />
      {blogPosts && <Blog posts={blogPosts} />}
      <OfficeMap />
    </Layout>
  )
}
